'use client';

import { createContext, useContext, useState, useCallback } from 'react';

import { getZIndexClass } from '@app/libs/zIndex';
import { cn } from '@libs/utils';

import {
  Toast,
  ToastContextType,
  ToastState,
  ToastConfig,
  ToastType,
  ToastProviderProps,
} from './Toast';

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const defaultToastConfig: Partial<ToastConfig> = {
  showCloseButton: true,
  position: 'top-right',
  animation: 'slide-in',
  duration: 5000, // 5 giây mặc định
};

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Record<string, ToastState>>({});

  const showToast = useCallback(
    (
      type: ToastType,
      message: string | React.ReactNode,
      title?: string,
      duration?: number
    ): string => {
      const toastId = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const config: ToastConfig = {
        ...defaultToastConfig,
        type,
        message,
        title,
        duration,
      } as ToastConfig;

      setToasts(prev => ({
        ...prev,
        [toastId]: {
          isVisible: true,
          config,
        },
      }));

      return toastId;
    },
    []
  );

  const hideToast = useCallback((id: string) => {
    setToasts(prev => {
      const newToasts = { ...prev };
      if (newToasts[id]) {
        newToasts[id] = {
          ...newToasts[id],
          isVisible: false,
        };
      }
      return newToasts;
    });

    // Remove toast from state after animation completes
    setTimeout(() => {
      setToasts(prev => {
        const newToasts = { ...prev };
        delete newToasts[id];
        return newToasts;
      });
    }, 300);
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts(prev => {
      const newToasts = { ...prev };
      Object.keys(newToasts).forEach(id => {
        newToasts[id] = {
          ...newToasts[id],
          isVisible: false,
        };
      });
      return newToasts;
    });

    // Remove all toasts after animation completes
    setTimeout(() => {
      setToasts({});
    }, 300);
  }, []);

  const value: ToastContextType = {
    toasts,
    showToast,
    hideToast,
    clearAllToasts,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onClose={hideToast} />
    </ToastContext.Provider>
  );
}

// Toast container component
interface ToastContainerProps {
  toasts: Record<string, ToastState>;
  onClose: (id: string) => void;
}

function ToastContainer({ toasts, onClose }: ToastContainerProps) {
  const visibleToasts = Object.entries(toasts).filter(([_, toast]) => toast.isVisible);

  if (visibleToasts.length === 0) return null;

  return (
    <div
      className={cn(
        'fixed top-21 right-4 space-y-3 w-full max-w-[580px] pointer-events-none px-4 sm:px-0',
        getZIndexClass('toast')
      )}
    >
      {visibleToasts.map(([id, toastState]) => (
        <div key={id} className="pointer-events-auto">
          <Toast
            id={id}
            type={toastState.config.type}
            title={toastState.config.title}
            message={toastState.config.message}
            isVisible={toastState.isVisible}
            duration={toastState.config.duration}
            showCloseButton={toastState.config.showCloseButton}
            onClose={() => onClose(id)}
          />
        </div>
      ))}
    </div>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  const { showToast, hideToast, clearAllToasts } = context;

  const toast = {
    success: useCallback(
      (message: string | React.ReactNode, title?: string, duration: number = 5000) =>
        showToast('success', message, title, duration),
      [showToast]
    ),

    error: useCallback(
      (message: string | React.ReactNode, title?: string, duration: number = 5000) =>
        showToast('danger', message, title, duration),
      [showToast]
    ),

    warning: useCallback(
      (message: string | React.ReactNode, title?: string, duration: number = 5000) =>
        showToast('warning', message, title, duration),
      [showToast]
    ),

    info: useCallback(
      (message: string | React.ReactNode, title?: string, duration: number = 5000) =>
        showToast('info', message, title, duration),
      [showToast]
    ),

    dismiss: hideToast,
    clear: clearAllToasts,
  };

  return toast;
}
